import { FormControl, Stack, TextField, Typography } from "@mui/material";
import WhiteCard from "../WhiteCard";
import { usePrescription } from "src/context/PrescriptionProvider";
import { FormEvent, useCallback, useMemo, useState } from "react";
import { useGetPharmacyByNameQuery } from "src/api/services/pharmacyApi";
import { skipToken } from "@reduxjs/toolkit/query";
import LoadingButton from "../Buttons/LoadingButton";
import { useUpdatePrescriptionMutation } from "src/api/services/prescriptionsApi";
import { useCreateOrderMutation } from "src/api/services/ordersApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  onSuccess: () => Promise<void>;
}

const productToInformation: Record<string, { form: string; pharmacy: string }> =
  {
    ec: { form: "pack(s)", pharmacy: "Amazon Pharmacy" },
  };

const SubmitOrder = ({ onSuccess }: Props) => {
  const [updatePrescription] = useUpdatePrescriptionMutation();
  const [createOrder] = useCreateOrderMutation();
  const [orderNumber, setOrderNumber] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const product = usePrescription((p) => p.product.name);
  const slug = usePrescription((p) => p.product.slug);
  const dispenseQuantity = usePrescription((p) => p.dispense_quantity);
  const prescriptionId = usePrescription((p) => p.id);
  const patientId = usePrescription((p) => p.patient_id);
  const productId = usePrescription((p) => p.product_id);
  const productCategoryId = usePrescription((p) => p.product_category_id);
  const invoiceId = usePrescription(
    (p) => (p.metadata as Record<string, string>)?.invoice_id,
  );
  const error = useSentryLogger("error");

  const productInformation = useMemo(
    () => productToInformation[slug] ?? null,
    [slug],
  );

  const pharmacyName = useMemo(
    () => productInformation?.pharmacy,
    [productInformation?.pharmacy],
  );

  const { data: pharmacy } = useGetPharmacyByNameQuery(
    pharmacyName ? pharmacyName : skipToken,
  );

  const quantity = useMemo(() => {
    const form = productInformation?.form ?? "";

    return `${dispenseQuantity} ${form}`.trim();
  }, [dispenseQuantity, productInformation?.form]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setLoading(true);

      try {
        await updatePrescription({
          id: prescriptionId,
          status: "prescribed",
          prescribed_at: new Date().toISOString(),
          instructions: [
            {
              value: "Take 1 tablet as needed.",
              amount: "1 tablet",
              dosage: "1.5 mg",
              duration: "1 day",
              interval: "day",
              interval_count: 1,
            },
          ],
          pharmacy_id: pharmacy?.id,
        }).unwrap();

        if (pharmacy != null) {
          await createOrder({
            prescription_id: prescriptionId,
            status: "sent_to_pharmacy",
            patient_id: patientId,
            pharmacy_id: pharmacy.id,
            product_id: productId,
            product_category_id: productCategoryId,
            invoice_id: invoiceId,
            pharmacy_order_id: orderNumber,
          }).unwrap();
        }
        onSuccess();
      } catch (err) {
        error(
          `Error submitting order for prescription: ${prescriptionId}`,
          err,
        );
      } finally {
        setLoading(false);
      }
    },
    [
      createOrder,
      error,
      invoiceId,
      onSuccess,
      orderNumber,
      patientId,
      pharmacy,
      prescriptionId,
      productCategoryId,
      productId,
      updatePrescription,
    ],
  );

  return (
    <WhiteCard width="100%" sx={{ background: "white" }}>
      <Stack gap={1}>
        <Typography sx={{ textDecoration: "underline" }}>
          {product} - {pharmacyName}
        </Typography>
        <Stack gap={2} component={"form"} onSubmit={handleSubmit}>
          <Typography>Quantity - {quantity}</Typography>
          <FormControl required>
            <TextField
              required
              label="Order #"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
            />
          </FormControl>
          <LoadingButton loading={loading} type="submit">
            Submit order
          </LoadingButton>
        </Stack>
      </Stack>
    </WhiteCard>
  );
};

export default SubmitOrder;
